/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import {
  getListSolicitud,
  createTicket,
  createKontrolTicket,
  getPortsAndLag,
} from "../request/request";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAppSelector } from "@redux/hooks";
import { getServiceLabel } from "../GetServiceLabel";
import { ServiceItem, ServiceType, TenantInfo } from "../Types";

interface LabelValue {
  label: string;
  value: string;
}

type BaseItem = {
  id: number;
  name?: string;
  subscriptionId?: string;
  customer?: TenantInfo;
};

interface IncidentReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated?: () => void;
}

/** Actualizamos productos si lo requieres **/
const productData: LabelValue[] = [
  { label: "CTX Reflector", value: "ctx-r" },
  { label: "Dashboard", value: "dashboard" },
  { label: "Kontrol Edge", value: "ke" },
];

const countryCodes: LabelValue[] = [
  { label: "United States (+1)", value: "+1" },
  { label: "Mexico (+52)", value: "+52" },
  { label: "Canada (+1)", value: "+1" },
];

const TABS = [
  { id: "serviceSelection", label: "Service selection" },
  { id: "requestInfo", label: "Request Info" },
  { id: "attachments", label: "Attachments & Response" },
];

const mapToServiceItems = <T extends BaseItem>(
  items: T[] | undefined,
  label: string,
  type: ServiceType
): ServiceItem[] => {
  const arr = Array.isArray(items) ? items : [];
  return arr.map((i) => ({
    id: i.id,
    name: i.name ?? `${label}-${i.id}`,
    subscriptionId: i.subscriptionId ?? null, // ← keep null for FWaaS
    type,
    customer: i.customer
      ? {
          id: i.customer.id,
          name: i.customer.name,
          nameDisplay: i.customer.nameDisplay,
        }
      : undefined,
  }));
};

export const IncidentReportModal: React.FC<IncidentReportModalProps> = ({
  isOpen,
  onClose,
  onTicketCreated,
}) => {
  const api = useAxiosAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  // Estados para formularios
  const [selectedProductLabel] = useState("");
  const [topics, setTopics] = useState<LabelValue[]>([]);
  const [selectedTopicLabel, setSelectedTopicLabel] = useState("");

  // Campo "AffectedServices" (Yes/No) => 10159 / 10160
  const [affectedServices, setAffectedServices] = useState("");

  const [cloud] = useState("CloudGlobal");
  const [region] = useState("us-east-1");
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("");
  const [description, setDescription] = useState("");

  const [sendResponse, setSendResponse] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("+1");
  const [contactPhoneNumber, setContactPhoneNumber] = useState("");

  const [attachmentFiles, setAttachmentFiles] = useState<FileList | null>(null);

  const [loadingTopics, setLoadingTopics] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isLoadingSomething = loadingTopics || isSaving;
  const overlay = useProcessingOverlay(isLoadingSomething);

  const [activeTab, setActiveTab] = useState<string>("serviceSelection");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertVariant, setAlertVariant] = useState<"success" | "error">(
    "success"
  );
  const [alertMessage, setAlertMessage] = useState("");
  const [serviceItems, setServiceItems] = useState<ServiceItem[]>([]);
    const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
      null
    );
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    string | null
  >(null);
  const [selectedServiceType, setSelectedServiceType] = useState<
    "port" | "internet" | "dci" | "cloud" | "fwaas" | null
  >(null);
  const [selectedServiceLabel, setSelectedServiceLabel] = useState<
    string | undefined
  >();

  // Check if the selected product is "Kontrol Edge"
  const globalProduct = useAppSelector(
    (state) => state.product.selectedProduct
  );
  const isKontrolEdge = globalProduct === "Kontrol Edge";

  const stripNonAlphanumeric = (str: string) => str.replace(/[\s\W_]+/g, "");
  const isValidEmail = (email: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  };

  useEffect(() => {
    if (!isKontrolEdge) {
      // If user changes product away from KE, reset
      setServiceItems([]);
      setSelectedSubscriptionId(null);
      setSelectedServiceType(null);
      return;
    }

    // Otherwise fetch the data
    const fetchData = async () => {
      try {
        const resp = await getPortsAndLag(api, tenantId);
        if (!resp?.data.data) {
          console.error("No data in response");
          return;
        }
        const {
          cloudConnectivity,
          datacenterInterconnect,
          fwaas,
          internet,
          ports,
        } = resp.data.data;
    
        const combined: ServiceItem[] = [
          ...mapToServiceItems(ports, "Port", "port"),
          ...mapToServiceItems(internet, "Internet", "internet"),
          ...mapToServiceItems(datacenterInterconnect, "Data center", "dci"),
          ...mapToServiceItems(cloudConnectivity, "Cloud", "cloud"),
          ...mapToServiceItems(fwaas, "FWaaS", "fwaas"),
        ];
    
        setServiceItems(combined);
      } catch (error) {
        console.error("Error fetching ports/lags =>", error);
      }
    }    
    fetchData();
  }, [isKontrolEdge, api, tenantId, isOpen]);

  /**
   * Validaciones
   */
  const isGeneralInfoValid = () => {
    // For Kontrol Edge, skip topic/request validation
    if (isKontrolEdge) {
      return true;
    }
    return (
      selectedTopicLabel.trim() !== "" &&
      // Campo de "AffectedServices"
      affectedServices.trim() !== ""
    );
  };

  const isAdditionalInfoValid = () => {
    if (isKontrolEdge) return priority.trim() !== "";
    const titleClean = stripNonAlphanumeric(title);
    const descClean = stripNonAlphanumeric(description);
    return (
      cloud.trim() !== "" &&
      region.trim() !== "" &&
      titleClean.length >= 5 &&
      descClean.length >= 10 &&
      priority.trim() !== ""
    );
  };

  const isAttachmentsValid = () => {
    if (isKontrolEdge) return true;
    if (!sendResponse) return true;
    const minName = contactName.trim().length >= 3;
    const validEmail = isValidEmail(contactEmail.trim());
    return minName && validEmail;
  };

  const canActivateTab = (tabId: string) => {
    switch (tabId) {
      case "serviceSelection":
        return true;
      case "requestInfo":
        return isGeneralInfoValid();
      case "attachments":
        return isGeneralInfoValid() && isAdditionalInfoValid();
      default:
        return false;
    }
  };

  const handleChangeTab = (tabId: string) => {
    if (canActivateTab(tabId)) {
      setActiveTab(tabId);
    }
  };

  /**
   * fetchTopics para "Topic"
   */
  const fetchTopics = async (productValue: string) => {
    try {
      setLoadingTopics(true);
      // Llamamos con type="Incident Report"
      const resp = await getListSolicitud(api, "Incident Report", productValue);
      setTopics(resp.data as LabelValue[]);
    } catch (error) {
      console.error("Error fetching topics =>", error);
    } finally {
      setLoadingTopics(false);
    }
  };

  useEffect(() => {
    if (!isKontrolEdge) {
      // For non-Kontrol Edge, fetch topics if needed.
      const found = productData.find((p) => p.label === globalProduct);
      if (found) {
        fetchTopics(found.value);
      }
    }
  }, [globalProduct, isKontrolEdge]);

  const handleSelectTopic = (topicLabel: string) => {
    setSelectedTopicLabel(topicLabel);
    console.log("selected topic laberl", topicLabel);
  };

  /**
   * Manejamos la selección en el combo "Are there service disruptions?"
   * con valores 10159 (Yes) / 10160 (No).
   */
  const handleServicesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAffectedServices(e.target.value);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachmentFiles(e.target.files);
    }
  };

  /**
   * Reset form
   */
  const resetFormState = () => {
    setTopics([]);
    setSelectedTopicLabel("");
    setAffectedServices("");
    setTitle("");
    setPriority("");
    setDescription("");
    setSendResponse(false);
    setContactName("");
    setContactEmail("");
    setSelectedCountryCode("+1");
    setContactPhoneNumber("");
    setAttachmentFiles(null);
    setActiveTab("serviceSelection");
  };

  const handleCloseModal = () => {
    resetFormState();
    onClose();
  };

  /**
   * Guardar ticket
   */
  const handleSave = async () => {
    if (!isAttachmentsValid()) return;
    setIsSaving(true);
    try {
      const foundProduct = productData.find(
        (p) => p.label === selectedProductLabel
      );
      const productValue = foundProduct ? foundProduct.value : "";
      const foundTopic = topics.find((t) => t.label === selectedTopicLabel);
      const topicValue = foundTopic ? foundTopic.value : "";

      // Convertimos la prioridad en número:
      //   Low(4), Medium(3), High(2), Urgent(1)
      const finalPriority = parseInt(priority, 10);
      const selected = serviceItems.find(
        (item) => getServiceLabel(item) === selectedServiceLabel
      );
      const subscriptionID =
        selected?.subscriptionId ?? selected?.name ?? String(selected?.id ?? "");

      if (isKontrolEdge) {
        const kontrolPayload = {
          requesttype: "INCIDENT_REPORT",
          summary: title,
          priority: finalPriority,
          description,
          subscriptionID,
          serviceType: selected?.type ?? null,
          files: attachmentFiles ? Array.from(attachmentFiles) : [],
        };
        //console.log("kontrolPayloadPayload  =>", kontrolPayload);
        const response = await createKontrolTicket(api, kontrolPayload);
        if (onTicketCreated) {
          onTicketCreated();
        }
        console.log("Kontrol ticket created =>", response.data);
      } else {
        const finalPayload = {
          Tipo: "Incident Report",
          product: productValue,
          cloud,
          region,
          TipoSolicitud: topicValue,
          AffectedServices: affectedServices,
          Resumen: title,
          Prioridad: finalPriority,
          Descripcion: description,
          files: attachmentFiles ? Array.from(attachmentFiles) : [],
          Nombre_de_contacto: contactName,
          Correo: contactEmail,
          phone_number: `${selectedCountryCode}${contactPhoneNumber.replace(
            /\s+/g,
            ""
          )}`,
        };
        //console.log("finalPayload  =>", finalPayload);
        const response = await createTicket(api, finalPayload);
        console.log("Incident ticket created =>", response.data);
      }
      setAlertVariant("success");
      setAlertMessage("¡Incident ticket created successfully!");
      setAlertOpen(true);
    } catch (err) {
      console.error("Error creating incident =>", err);
      setAlertVariant("error");
      setAlertMessage(
        "A problem occurred while creating the incident, please try again."
      );
      setAlertOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

    const getSelectedServiceName = () => {
      const selectedItem = serviceItems.find(
        (item) =>
          item.id === selectedServiceId && item.type === selectedServiceType
      );
      return selectedItem ? getServiceLabel(selectedItem) : "";
    };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      closeOnBackdropClick={false}
      size="lg"
      title={
        <div className="flex items-center gap-2">
          <span>Incident Report</span>
          <GenericTooltip
            text="Use this section to tell us about INCIDENTS or ISSUES of greater involvement."
            position="top"
          />
        </div>
      }
    >
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex space-x-4" aria-label="Tabs">
          {TABS.map((tab) => {
            const disabled = !canActivateTab(tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => handleChangeTab(tab.id)}
                disabled={disabled}
                className={`
                  px-3 py-2 text-sm font-medium focus:outline-none
                  ${
                    activeTab === tab.id
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : disabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-500 hover:text-gray-700"
                  }
                `}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "serviceSelection" && (
        <div className="space-y-4 text-sm text-gray-700">
          {isKontrolEdge && (
            <div className="flex gap-4">
              {/* If Kontrol Edge => show the new dropdown */}
              <div className="w-1/2">
                <label className="block mb-1 font-medium">Select Service</label>
                <GenericDropdown
                  items={serviceItems.map(getServiceLabel)}
                  // value={selectedServiceLabel}
                  value={getSelectedServiceName()}
                  onSelect={(selectedLabel: string) => {
                    const selected = serviceItems.find(
                      (item) => getServiceLabel(item) === selectedLabel
                    );
                    const fallbackSubId =
                    selected?.subscriptionId ?? selected?.name ?? String(selected?.id ?? "");
                    setSelectedServiceId(selected?.id ?? null);
                    setSelectedSubscriptionId(fallbackSubId || null);
                    setSelectedServiceType(selected?.type ?? null);
                    setSelectedServiceLabel(selectedLabel);
                  }}
                  defaultLabel="Select a service"
                  minWidthClass=""
                  maxWidthClass=""
                  widthClass="w-full"
                  dropdownMaxHeightClass="max-h-48"
                />
              </div>
              <div className="w-1/2">
                <label className="block mb-1 font-medium">
                  Are there service disruptions?{" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="Affected"
                  required
                  value={affectedServices}
                  onChange={handleServicesChange}
                  className="w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white"
                >
                  <option hidden value="">
                    Select
                  </option>
                  <option value="10159">Yes</option>
                  <option value="10160">No</option>
                </select>
              </div>
            </div>
          )}

          {!isKontrolEdge && (
            <div className="flex gap-4">
              <div className="w-1/2">
                <label className="block mb-1 font-medium">
                  Topic <span className="text-red-500">*</span>
                </label>
                <GenericDropdown
                  items={topics.map((t) => t.label)}
                  value={selectedTopicLabel}
                  onSelect={handleSelectTopic}
                  defaultLabel="No options available"
                  minWidthClass=""
                  maxWidthClass=""
                  widthClass="w-full"
                />
              </div>
              <div className="w-1/2">
                <label className="block mb-1 font-medium">
                  Are there service disruptions?{" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="Affected"
                  required
                  value={affectedServices}
                  onChange={handleServicesChange}
                  className="w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white"
                >
                  <option hidden value="">
                    Select
                  </option>
                  <option value="10159">Yes</option>
                  <option value="10160">No</option>
                </select>
              </div>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <GenericButton
              label="Next"
              variant="brand"
              onClick={() => handleChangeTab("requestInfo")}
              disabled={!isGeneralInfoValid()}
            />
          </div>
        </div>
      )}

      {activeTab === "requestInfo" && (
        <div className="space-y-4 text-sm text-gray-700">
          <div className="flex gap-4">
            <div className="w-1/2">
              <label className="block mb-1 font-medium">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                required
                id="Prioridad"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white"
              >
                <option hidden value="">
                  Select
                </option>
                <option className="text-success" value="4">
                  Low
                </option>
                <option className="text-warning" value="3">
                  Medium
                </option>
                <option className="text-danger" value="2">
                  High
                </option>
                {affectedServices !== "10160" && (
                  <option className="text-light-danger bg-danger" value="10000">
                    Urgent
                  </option>
                )}
              </select>
            </div>
          </div>

          {!isKontrolEdge && (
            <div className="flex gap-4">
              <div className="w-1/2">
                <label className="block mb-1 font-medium">
                  Cloud <span className="text-red-500">*</span>
                </label>
                <GenericInput value={cloud} disabled />
              </div>
              <div className="w-1/2">
                <label className="block mb-1 font-medium">
                  Region <span className="text-red-500">*</span>
                </label>
                <GenericInput value={region} disabled />
              </div>
            </div>
          )}

          <div>
            <label className="block mb-1 font-medium flex items-center gap-1">
              Title <span className="text-red-500">*</span>
              <GenericTooltip
                text="At least 5 characters (ignoring spaces & punctuation)."
                position="top"
              />
            </label>
            <GenericInput
              placeholder="Short summary of the incident"
              value={title}
              onChange={setTitle}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium flex items-center gap-1">
              Description <span className="text-red-500">*</span>
              <GenericTooltip
                text="At least 10 characters (ignoring spaces & punctuation)."
                position="top"
              />
            </label>
            <textarea
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-sm text-sm"
              placeholder="Describe what happened"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex justify-end pt-2 gap-2">
            <GenericButton
              label="Back"
              variant="secondary"
              onClick={() => setActiveTab("serviceSelection")}
            />
            <GenericButton
              label="Next"
              variant="brand"
              onClick={() => handleChangeTab("attachments")}
              disabled={!isAdditionalInfoValid()}
            />
          </div>
        </div>
      )}

      {activeTab === "attachments" && (
        <div className="space-y-4 text-sm text-gray-700">
          <div>
            <label className="block mb-1 font-medium">
              Attach (screenshots, logs, etc.)
            </label>
            <div className="flex items-center gap-2">
              <label
                htmlFor="file-upload"
                className="
                  cursor-pointer 
                  px-3 py-2 
                  border border-gray-300 
                  rounded-sm 
                  bg-gray-50
                  text-sm 
                  hover:bg-gray-100
                "
              >
                {attachmentFiles ? "Change Files" : "Choose Files"}
              </label>
              <input
                id="file-upload"
                type="file"
                className="hidden"
                multiple
                onChange={handleFileChange}
              />
              <span className="text-sm text-gray-500">
                {attachmentFiles
                  ? Array.from(attachmentFiles)
                      .map((file) => file.name)
                      .join(", ")
                  : "No file chosen"}
              </span>
            </div>
          </div>
          {!isKontrolEdge && (
            <div className="space-y-2">
              <div className="flex items-center justify-between mt-3">
                <span className="font-medium text-sm text-gray-800">
                  I want to send a response of the case to another person
                </span>
                <GenericSwitch
                  checked={sendResponse}
                  onChange={setSendResponse}
                />
              </div>

              {sendResponse && (
                <div className="space-y-4 mt-2 border border-gray-200 rounded p-3">
                  <div className="flex gap-4">
                    <div className="w-1/2">
                      <label className="block mb-1 font-medium flex items-center gap-1">
                        Contact Name <span className="text-red-500">*</span>
                        <GenericTooltip
                          text="At least 3 characters."
                          position="top"
                        />
                      </label>
                      <GenericInput
                        placeholder="Name"
                        value={contactName}
                        onChange={setContactName}
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="block mb-1 font-medium flex items-center gap-1">
                        Contact Email <span className="text-red-500">*</span>
                        <GenericTooltip
                          text="Must be a valid email format."
                          position="top"
                        />
                      </label>
                      <GenericInput
                        placeholder="Email"
                        value={contactEmail}
                        onChange={setContactEmail}
                        type="email"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-1/2">
                      <label className="block mb-1 font-medium">
                        Country Code
                      </label>
                      <GenericDropdown
                        items={countryCodes.map((c) => c.label)}
                        value={
                          countryCodes.find(
                            (c) => c.value === selectedCountryCode
                          )?.label || "United States (+1)"
                        }
                        onSelect={(label) => {
                          const found = countryCodes.find(
                            (c) => c.label === label
                          );
                          if (found) {
                            setSelectedCountryCode(found.value);
                          }
                        }}
                        defaultLabel="Select country code"
                        minWidthClass=""
                        maxWidthClass=""
                        widthClass="w-full"
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="block mb-1 font-medium">
                        Phone Number
                      </label>
                      <GenericInput
                        placeholder="Phone number"
                        value={contactPhoneNumber}
                        onChange={setContactPhoneNumber}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-2 gap-2">
            <GenericButton
              label="Back"
              variant="secondary"
              onClick={() => setActiveTab("requestInfo")}
            />
            <GenericButton
              label="Save"
              variant="brand"
              onClick={handleSave}
              disabled={!isAttachmentsValid() || isSaving}
            />
          </div>
        </div>
      )}

      {overlay}

      <GenericAlert
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={() => {
          setAlertOpen(false);
          if (alertVariant === "success") {
            handleCloseModal();
          }
        }}
        variant={alertVariant}
        title={alertVariant === "success" ? "Incident Report Created" : "Error"}
        description={alertMessage}
        confirmLabel="OK"
      />
    </GenericModal>
  );
};

export default IncidentReportModal;
